/**
 * Swaps the tab favicon, apple-touch-icon and PWA manifest at runtime when
 * an owner-uploaded app icon exists. Falls back to the built-in Genelco
 * mark when the branding is removed.
 *
 * - Mount this once at the App root.
 * - It subscribes to branding meta changes, so re-uploads bust the cache
 *   via ?v=<version> without a page reload.
 * - Manifest link is only pointed at the dynamic endpoint when a favicon
 *   is uploaded (so we don't break installability during first paint if
 *   the API is briefly unreachable).
 */
import { useEffect } from "react";
import {
  loadBrandingMeta,
  subscribeBranding,
  brandingFaviconUrl,
  brandingManifestUrl,
} from "@/lib/branding";

const BUILTIN = {
  icon: "/favicon.png",
  apple: "/apple-touch-icon.png",
  manifest: "/manifest.webmanifest",
};

function ensureLink(rel, sizes = null) {
  let selector = `link[rel="${rel}"]`;
  if (sizes) selector += `[sizes="${sizes}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (sizes) el.setAttribute("sizes", sizes);
    document.head.appendChild(el);
  }
  return el;
}

function applyMeta(meta) {
  const fav = meta && meta.favicon;
  const iconLink = ensureLink("icon");
  const appleLink = ensureLink("apple-touch-icon", "180x180");
  const manifestLink = ensureLink("manifest");

  if (fav) {
    iconLink.setAttribute("type", "image/png");
    iconLink.setAttribute("href", brandingFaviconUrl(32, fav.version));
    appleLink.setAttribute("href", brandingFaviconUrl(180, fav.version));
    manifestLink.setAttribute("href", brandingManifestUrl(fav.version));
  } else {
    iconLink.setAttribute("type", "image/png");
    iconLink.setAttribute("href", BUILTIN.icon);
    appleLink.setAttribute("href", BUILTIN.apple);
    manifestLink.setAttribute("href", BUILTIN.manifest);
  }
}

export function useDynamicFavicon() {
  useEffect(() => {
    // Fire an initial fetch (memoised) so first paint has correct icons.
    loadBrandingMeta().then(applyMeta);
    // Re-apply whenever branding meta changes (upload / delete).
    return subscribeBranding(applyMeta);
  }, []);
}
